'use client';

import {
  BiometricEvidenceDialog,
  CaptureViewer,
  EvidenceFindings,
  EvidenceProvenance,
  OutcomeReason,
  RequestLinkAction,
  TBiometricConfidenceLevel,
  TCapture,
  TCaptureEntry,
  TLivenessDetermination,
} from '@verifime/components';
import { ReactElement, ReactNode } from 'react';
import { TRequestState } from './requestState';
import { useBiometricEvidence } from './useBiometricEvidence';

export type TBiometricEvidenceProps = {
  customerId?: string;
  open: boolean;
  onClose: () => void;
  /** The level the card is showing. The dialog derives its summary sentence and outcome from it. */
  level: TBiometricConfidenceLevel;
  /** Shared with the card rather than fetched again, so the two can never disagree. */
  requestState: TRequestState;
  /** The dialog's buttons. The request link is added by this view, on the left of them. */
  actions?: ReactNode;
  onError?: (error: unknown) => void;
  'data-testid'?: string;
};

/**
 * The capture stages, measured off the frozen prototype.
 *
 * Supplied by this view rather than defaulted inside `CaptureViewer`, which takes a box per entry on
 * purpose: a face check and an identity document portrait are routinely different shapes, so there
 * is no single box every capture can share. The box also fixes the stage's footprint whatever is in
 * it, so a missing slot holds its place and a poster-to-video swap never shifts the row.
 */
const FACE_CHECK_BOX = { width: 300, height: 400 };
const DOCUMENT_PORTRAIT_BOX = { width: 156, height: 196 };

/** Stated once for the whole set, so a reader is not left wondering why the sizes differ. */
const CAPTURE_NOTE = 'Captures are shown at their true size and are never enlarged.';

/**
 * Builds one capture entry, present or visibly absent.
 *
 * A capture with no URL renders as `missing` rather than being dropped. Under the biometric model a
 * face check can complete while the identity document portrait never scanned across, which is the
 * whole of what `InProgress` means, so the empty slot is the answer to the question the dialog
 * exists to answer: which input is missing.
 */
function captureEntry(
  id: string,
  label: string,
  box: { width: number; height: number },
  src?: string,
  extras: Partial<Extract<TCapture, { kind: 'capture' }>> = {},
): TCaptureEntry {
  return {
    id,
    label,
    box,
    capture: src ? { kind: 'capture', src, ...extras } : { kind: 'missing' },
  };
}

/**
 * The biometric evidence dialog, with its own data.
 *
 * **Gathers nothing until it is open.** The three calls behind it are beyond what the cards need,
 * and most customers a client opens are never clicked through to.
 *
 * **Every element appears only when its data does.** The play control exists only when there is a
 * video, the similarity reads as a dash when no comparison was made, and provenance fields the
 * customer has no history for read as dashes too. That is deliberate in place of a loading state per
 * panel: the dialog is opened on demand, after the cards have already settled, and three more state
 * machines would be three more ways to be wrong.
 */
export default function BiometricEvidence({
  customerId,
  open,
  onClose,
  level,
  requestState,
  actions,
  onError,
  'data-testid': dataTestId,
}: Readonly<TBiometricEvidenceProps>): ReactElement {
  const evidence = useBiometricEvidence({ customerId, enabled: open, onError });

  const captures: TCaptureEntry[] = [
    captureEntry('face-check', 'Face check', FACE_CHECK_BOX, evidence.faceCaptureUrl, {
      onDark: true,
      videoSrc: evidence.faceVideoUrl,
    }),
    captureEntry(
      'document-portrait',
      'ID portrait',
      DOCUMENT_PORTRAIT_BOX,
      evidence.documentPortraitUrl,
    ),
  ];

  return (
    <BiometricEvidenceDialog
      open={open}
      onClose={onClose}
      level={level}
      heading="Biometric checks"
      data-testid={dataTestId}
      media={
        <CaptureViewer
          captures={captures}
          note={CAPTURE_NOTE}
          onCaptureError={(id) => onError?.(new Error(`Capture failed to load: ${id}`))}
        />
      }
      actions={
        // Two nodes puts the link on the left and the buttons hard right; one node leaves the
        // buttons exactly where they sit with a link present, which is what a reader moving between
        // customers needs. The link is offered only when a task is open to be completed.
        requestState.requestUrl ? (
          <>
            <RequestLinkAction
              url={requestState.requestUrl}
              onCopyError={(error) => onError?.(error)}
            />
            {actions}
          </>
        ) : (
          actions
        )
      }
    >
      <EvidenceFindings
        similarityScore={evidence.similarityScore}
        liveness={evidence.liveness as TLivenessDetermination}
      />
      <OutcomeReason level={level} liveness={evidence.liveness as TLivenessDetermination} />
      <EvidenceProvenance
        completedOn={evidence.completedOn}
        requestedOn={requestState.requestedOn}
        requestedBy={requestState.requestedBy}
      />
    </BiometricEvidenceDialog>
  );
}
